package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo

/**
 * Standard header for every quiz question: the question text in one card, and — only when
 * [wordContent] is provided — the related word/hieroglyphs in a second card right below it.
 * Both cards share the same shape and carry no border; the question card uses a more prominent
 * container tone than the word card so the two are visually distinct without relying on outlines.
 */
@Composable
fun QuestionBox(
    question: String,
    modifier: Modifier = Modifier,
    questionTestTag: String? = null,
    wordContent: (@Composable () -> Unit)? = null
) {
    val deviceInfo = rememberDeviceInfo()
    val spacer = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 1.dp
        DeviceClass.PHONE_MEDIUM -> 2.dp
        else -> 4.dp
    }
    val padding = when (deviceInfo.deviceClass) {
        DeviceClass.PHONE_SMALL -> 2.dp
        DeviceClass.PHONE_MEDIUM -> 4.dp
        else -> 8.dp
    }
    Column(
        modifier = modifier.fillMaxWidth().padding(horizontal = 16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        if (question.isNotBlank()) {
            Spacer(Modifier.height(spacer))
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
                )
            ) {
                Text(
                    text = question,
                    style = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) {
                        MaterialTheme.typography.bodyMedium
                    } else {
                        MaterialTheme.typography.bodyLarge
                    },
                    textAlign = TextAlign.Center,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(padding)
                        .then(
                            if (questionTestTag != null) {
                                Modifier.semantics { testTag = questionTestTag }
                            } else {
                                Modifier
                            }
                        )
                )
            }
        }

        if (wordContent != null) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceContainerLow
                )
            ) {
                Box(
                    modifier = Modifier.fillMaxWidth().padding(padding),
                    contentAlignment = Alignment.Center
                ) {
                    wordContent()
                }
            }
        }
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun QuestionBoxTextOnlyPreview() {
    AppTheme {
        Surface {
            QuestionBox(
                question = "Gli antichi Egizi usavano i geroglifici per la salamoia?",
                modifier = Modifier.padding(vertical = 16.dp)
            )
        }
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun QuestionBoxWithWordPreview() {
    AppTheme {
        Surface {
            QuestionBox(
                question = "Cosa vuol dire?",
                modifier = Modifier.padding(vertical = 16.dp),
                wordContent = {
                    MdCWord(word = "M17-A1-D2h2:Z1", rtl = false, cart = false, size = 72.dp)
                }
            )
        }
    }
}
